// WellInspectionCard.js
// This is the well inspection card component for the inspections tab

import React, { useState, useRef, useEffect, useMemo } from "react";
import Select from "react-select";
// Importing the useAppStore for the notification modal
// Adding the appStore
import { useAppStore } from "../../../../../store/appStore";
// Importing other shit like some constants we will use over and over again.
import {
  INSPECTION_OPTIONS,
  BOTTOM_CONDITIONS_OPTIONS,
  INSPECTION_FIELDS_TO_VALIDATE,
} from "../../../../../constants/samplingEventConstants";
// Components
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import NaplSection from "./NaplSection";
import styles from "../InspectionsTab.module.css";

// Well Inspection Card Component
export const WellInspectionCard = ({
  well,
  isExpanded,
  onExpandWell,
  user,
  allEmployees,
  userAccessLevel,
  actions,
  ui,
  emitPatch,
}) => {
  // UI State updates and handlers, and notification stuff
  const showNotificationModal = useAppStore(
    (s) => s.notificationActions.showNotification
  );

  const handleCloseConfirmationModal = () => {
    actions.closeConfirmationModal();
  };

  // Setting disable const for if the well inspection is completed and the user does not have the userAcessLevel
  const disableInspectionUpdates =
    well.inspectionCompleted && !userAccessLevel ? true : false;

  // Helper function to get the currently selected option for a field so we don't have to store value and label in our dataclass, UI update class basicall
  const getSelectedInspectionOption = (field) => {
    const value = well[field];
    return value
      ? INSPECTION_OPTIONS.find((option) => option.value === value)
      : null;
  };

  const getSelectedBottomConditionsOption = (field) => {
    const value = well[field];
    return value
      ? BOTTOM_CONDITIONS_OPTIONS.find((option) => option.value === value)
      : null;
  };

  // State for dealing with the error list if it is returned by an action
  const [errorList, setErrorList] = useState([]);

  // Function to get the class name for a field based on if it has an error in our errorList
  const getFieldClassName = (field) => {
    const error = errorList.find((error) => error.field === field);
    return error
      ? `${styles.inspectionControl} ${styles.fieldInvalid}`
      : styles.inspectionControl;
  };

  // Function to find the user name for a given employee id, used to display who completed an inspection, displayed in ribbon for each inspection well card.
  const findUserName = (employeeId) => {
    const employee = allEmployees.find(
      (employee) => employee.employee_id === employeeId
    );
    return employee ? employee.name : "Unknown";
  };

  // Data update functions
  // Function to handle date changes/selections
  const handleDateChange = (field, date) => {
    actions.updateWellInspection(well.id, [{ field, value: date }], emitPatch);
  };

  // Function to handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    actions.updateWellInspection(well.id, [{ field, value }], emitPatch);
  };

  // Function to handle well inspection updates from select options
  const handleSelectChange = (field, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    actions.updateWellInspection(well.id, [{ field, value }], emitPatch);
  };

  // Function to handle well inspection onChange updates for text input fields so we don't overload the server with text inputs
  const handleTextInputChange = (field, value) => {
    actions.updateWellInspection(well.id, [{ field, value }]);
  };

  // Function to handle well inspection onBlur updates for text fields
  const handleTextInputOnBlur = (field, value) => {
    actions.updateWellInspection(well.id, [{ field, value }], emitPatch);
  };

  // Function to handle well inspection numeric input changes
  const handleNumericInput = (field, value) => {
    // Allow empty string, digits, and one decimal point
    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }

    // Store the raw value during typing (no formatting)
    actions.updateWellInspection(
      well.id,
      [{ field, value: value === "" ? null : value }],
      null // Don't emit patch while typing
    );
  };

  // Function to handle blur - formats value and sends patch to server
  const handleNumericBlur = (field, value) => {
    let formattedValue = null;

    if (value !== "" && value !== null && value !== undefined) {
      // Parse and format to 2 decimal places on blur
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        formattedValue = parsed.toFixed(2);
      }
    }

    // Update with formatted value and emit patch to server
    actions.updateWellInspection(
      well.id,
      [{ field, value: formattedValue }],
      emitPatch
    );
  };

  const handleConfirmMarkComplete = () => {
    actions.updateWellInspection(
      well.id,
      [
        { field: "inspectionCompleted", value: true },
        { field: "inspectionCompletedBy", value: user.employee_id },
        { field: "inspectionCompletedDate", value: new Date() },
      ],
      emitPatch
    );
    actions.closeConfirmationModal();
  };

  const handleValidateAndMarkComplete = () => {
    // Validate all required fields
    const errorList = actions.validateInspectionAndMarkComplete(
      well.id,
      INSPECTION_FIELDS_TO_VALIDATE
    );

    if (errorList.length > 0) {
      setErrorList(errorList);
      // Get list of "labels" from errorList
      const errorLabels = errorList.map((error) => error.label).join(", ");
      // Show the notification modal and list out the errors
      showNotificationModal({
        title: "Validation Errors",
        type: "error",
        message: `Please fill out the following fields: ${errorLabels}`,
        errors: errorList,
      });
      return;
    } else {
      actions.openConfirmationModal(well.id);
    }
  };

  return (
    <div
      className={`${styles.wellCard} ${
        well.inspectionCompleted ? styles.wellCardCompleted : ""
      }`}
    >
      <div className={styles.wellCardHeader} onClick={onExpandWell}>
        <h3>
          {well.wellId}
          {well.inspectionCompleted && " ✓ "}
          {well.inspectionCompletedBy.length > 0 &&
            findUserName(well.inspectionCompletedBy)}
          {well.inspectionCompleted &&
            well.inspectionCompletedDate &&
            `  ${well.inspectionCompletedDate}`}
        </h3>
        <span className={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</span>
      </div>

      {isExpanded && (
        <div className={styles.wellCardContent}>
          <div className={styles.inspectionSection}>
            <h4>Well Inspection</h4>
            {/* This is where the inspection completed date will be set. */}
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionFieldDate}>
                <label htmlFor={`inspectionCompletedDate-${well.id}`}>
                  Inspection Completed Date:
                </label>
                <SamplingEventDatePicker
                  id={`inspectionCompletedDate-${well.id}`}
                  selected={well.inspectionCompletedDate}
                  onChange={(date) =>
                    handleDateChange("inspectionCompletedDate", date)
                  }
                  className={styles.inspectionControlDate}
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>

            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`facilityMap-${well.id}`}>
                  1. Is the well on a facility map and easy to find?
                </label>
                <Select
                  id={`facilityMap-${well.id}`}
                  value={getSelectedInspectionOption("facilityMapEasyToFind")}
                  onChange={(selected) =>
                    handleSelectChange("facilityMapEasyToFind", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("facilityMapEasyToFind")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>

              <div className={styles.inspectionField}>
                <label htmlFor={`aboveGround-${well.id}`}>
                  2. Is the well flush mount or above ground?
                </label>
                <Select
                  id={`aboveGround-${well.id}`}
                  value={getSelectedInspectionOption("aboveGroundFlushMount")}
                  onChange={(selected) =>
                    handleSelectChange("aboveGroundFlushMount", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("aboveGroundFlushMount")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>

            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`damage-${well.id}`}>
                  3. Is the well free of physical damage?
                </label>
                <Select
                  id={`damage-${well.id}`}
                  value={getSelectedInspectionOption("freeOfDamage")}
                  onChange={(selected) =>
                    handleSelectChange("freeOfDamage", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("freeOfDamage")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>

              <div className={styles.inspectionField}>
                <label htmlFor={`legibleId-${well.id}`}>
                  4. Is a legible well ID present?
                </label>
                <Select
                  id={`legibleId-${well.id}`}
                  value={getSelectedInspectionOption("legibleWellId")}
                  onChange={(selected) =>
                    handleSelectChange("legibleWellId", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("legibleWellId")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>

            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`protected-${well.id}`}>
                  5. Is the casing protected by posts?
                </label>
                <Select
                  id={`protected-${well.id}`}
                  value={getSelectedInspectionOption("casingProtected")}
                  onChange={(selected) =>
                    handleSelectChange("casingProtected", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("casingProtected")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
              <div className={styles.inspectionField}>
                <label htmlFor={`secure-${well.id}`}>
                  6. Is the area free of weeds and trash?
                </label>
                <Select
                  id={`weeds-${well.id}`}
                  value={getSelectedInspectionOption("weedsTrash")}
                  onChange={(selected) =>
                    handleSelectChange("weedsTrash", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("weedsTrash")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`secure-${well.id}`}>
                  7. Is the casing secure (attempt to move)?
                </label>
                <Select
                  id={`secure-${well.id}`}
                  value={getSelectedInspectionOption("casingSecure")}
                  onChange={(selected) =>
                    handleSelectChange("casingSecure", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("casingSecure")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
              <div className={styles.inspectionField}>
                <label htmlFor={`cracked-${well.id}`}>
                  8. Is the surface pad/seal eroded or cracked?
                </label>
                <Select
                  id={`cracked-${well.id}`}
                  value={getSelectedInspectionOption("surfacePadCracked")}
                  onChange={(selected) =>
                    handleSelectChange("surfacePadCracked", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("surfacePadCracked")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`ponding-${well.id}`}>
                  9. Is the surface pad prone to water ponding?
                </label>
                <Select
                  id={`ponding-${well.id}`}
                  // The names are slightly different, removed the "to" so it matches the backend, leave as is for now.
                  value={getSelectedInspectionOption(
                    "surfacePadProneWaterPonding"
                  )}
                  onChange={(selected) =>
                    handleSelectChange("surfacePadProneWaterPonding", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("surfacePadProneWaterPonding")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
              <div className={styles.inspectionField}>
                <label htmlFor={`wellSecure-${well.id}`}>
                  10. Is the well secure (locked and void of gaps)?
                </label>
                <Select
                  id={`wellSecure-${well.id}`}
                  value={getSelectedInspectionOption("wellSecureLocked")}
                  onChange={(selected) =>
                    handleSelectChange("wellSecureLocked", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("wellSecureLocked")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`gauging-${well.id}`}>
                  11. Are survey/gauging marks present?
                </label>
                <Select
                  id={`gauging-${well.id}`}
                  value={getSelectedInspectionOption("surveyGaugingMarks")}
                  onChange={(selected) =>
                    handleSelectChange("surveyGaugingMarks", selected)
                  }
                  options={INSPECTION_OPTIONS}
                  className={getFieldClassName("surveyGaugingMarks")}
                  placeholder="Select an option"
                  isClearable
                  isDisabled={disableInspectionUpdates}
                />
              </div>
              <div className={styles.inspectionField}>
                <label htmlFor={`bottomConditions-${well.id}`}>
                  12. Well Bottom Conditions:
                </label>
                <Select
                  id={`bottomConditions-${well.id}`}
                  value={getSelectedBottomConditionsOption("bottomConditions")}
                  onChange={(selected) =>
                    handleSelectChange("bottomConditions", selected)
                  }
                  options={BOTTOM_CONDITIONS_OPTIONS}
                  className={getFieldClassName("bottomConditions")}
                  placeholder="Select an option"
                  isClearable={true}
                  isDisabled={disableInspectionUpdates}
                />
              </div>
            </div>

            <div className={styles.commentsField}>
              <label htmlFor={`comments-${well.id}`}>13. Comments</label>
              <textarea
                id={`comments-${well.id}`}
                className={styles.commentsTextarea}
                placeholder="Enter any comments about the well inspection here..."
                value={well.inspectionComments || ""}
                onChange={(e) =>
                  handleTextInputChange("inspectionComments", e.target.value)
                }
                onBlur={(e) =>
                  handleTextInputOnBlur("inspectionComments", e.target.value)
                }
                disabled={disableInspectionUpdates}
              />
            </div>

            <h4>NAPL Inspection</h4>
            <NaplSection
              well={well}
              handleCheckboxChange={handleCheckboxChange}
              handleTextInputChange={handleTextInputChange}
              handleTextInputOnBlur={handleTextInputOnBlur}
              handleNumericChange={handleNumericInput}
              handleNumericBlur={handleNumericBlur}
              disableInspectionUpdates={disableInspectionUpdates}
            />

            <h4>Well Elevation Data</h4>
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionMeasurementField}>
                <label htmlFor={`totalDepth-${well.id}`}>
                  As Built Total Depth (Ft BTOC):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id={`totalDepth-${well.id}`}
                  name="totalDepth"
                  value={well.totalDepth || ""}
                  disabled
                  className={styles.inspectionControl}
                />
              </div>
            </div>
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionMeasurementField}>
                <label htmlFor={`measuredTotalDepth-${well.id}`}>
                  Measured Total Depth (Ft BTOC):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id={`measuredTotalDepth-${well.id}`}
                  name="measuredTotalDepth"
                  value={well.measuredTotalDepth ?? ""}
                  onChange={(e) =>
                    handleNumericInput("measuredTotalDepth", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("measuredTotalDepth", e.target.value)
                  }
                  onKeyDown={(e) => {
                    // Prevent non-numeric entries
                    if (
                      e.key === "-" ||
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0.00"
                  disabled={
                    well.measuredTotalDepthNm || disableInspectionUpdates
                  }
                  className={styles.inspectionControl}
                />
              </div>
              <div className={styles.notMeasuredCheckbox}>
                <label>
                  <input
                    type="checkbox"
                    checked={well.measuredTotalDepthNm || false}
                    onChange={(e) =>
                      handleCheckboxChange(
                        "measuredTotalDepthNm",
                        e.target.checked
                      )
                    }
                    disabled={disableInspectionUpdates}
                  />
                  Not Measured
                </label>
              </div>
            </div>
            {well.measuredTotalDepthNm && (
              <div className={styles.inspectionFieldRow}>
                <div className={styles.inspectionMeasurementReasonField}>
                  <label htmlFor={`measuredTotalDepthNmReason-${well.id}`}>
                    Reason for Not Measuring:
                  </label>
                  <input
                    type="text"
                    id={`measuredTotalDepthNmReason-${well.id}`}
                    name="measuredTotalDepthNmReason"
                    value={well.measuredTotalDepthNmReason || ""}
                    onChange={(e) =>
                      handleTextInputChange(
                        "measuredTotalDepthNmReason",
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur(
                        "measuredTotalDepthNmReason",
                        e.target.value
                      )
                    }
                    className={styles.inspectionControl}
                    placeholder="Enter reason for not measuring"
                    disabled={disableInspectionUpdates}
                  />
                </div>
              </div>
            )}
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionMeasurementField}>
                <label htmlFor={`measuredDepthToWater-${well.id}`}>
                  Measured Depth to Water (Ft BTOC):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id={`measuredDepthToWater-${well.id}`}
                  name="measuredDepthToWater"
                  value={well.measuredDepthToWater || ""}
                  onChange={(e) =>
                    handleNumericInput("measuredDepthToWater", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("measuredDepthToWater", e.target.value)
                  }
                  onKeyDown={(e) => {
                    // Prevent non-numeric entries
                    if (
                      e.key === "-" ||
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0.00"
                  disabled={
                    well.measuredDepthToWaterNm || disableInspectionUpdates
                  }
                  className={styles.inspectionControl}
                />
              </div>
              <div className={styles.notMeasuredCheckbox}>
                <label>
                  <input
                    type="checkbox"
                    checked={well.measuredDepthToWaterNm || false}
                    onChange={(e) =>
                      handleCheckboxChange(
                        "measuredDepthToWaterNm",
                        e.target.checked
                      )
                    }
                    disabled={disableInspectionUpdates}
                  />
                  Not Measured
                </label>
              </div>
            </div>
            {well.measuredDepthToWaterNm && (
              <div className={styles.inspectionFieldRow}>
                <div className={styles.inspectionMeasurementReasonField}>
                  <label htmlFor={`measuredDepthToWaterNmReason-${well.id}`}>
                    Reason for Not Measuring:
                  </label>
                  <input
                    type="text"
                    id={`measuredDepthToWaterNmReason-${well.id}`}
                    name="measuredDepthToWaterNmReason"
                    value={well.measuredDepthToWaterNmReason || ""}
                    onChange={(e) =>
                      handleTextInputChange(
                        "measuredDepthToWaterNmReason",
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur(
                        "measuredDepthToWaterNmReason",
                        e.target.value
                      )
                    }
                    className={styles.inspectionControl}
                    placeholder="Enter reason for not measuring"
                    disabled={disableInspectionUpdates}
                  />
                </div>
              </div>
            )}
            <div className={styles.inspectionFieldRow}>
              <div className={styles.inspectionField}>
                <label htmlFor={`inspectionFeaturesDepths-${well.id}`}>
                  Ring Gauge Inspection Features Depths:
                </label>
                <textarea
                  id={`inspectionFeaturesDepths-${well.id}`}
                  name="inspectionFeaturesDepths"
                  value={well.inspectionFeaturesDepths || ""}
                  onChange={(e) =>
                    handleTextInputChange(
                      "inspectionFeaturesDepths",
                      e.target.value
                    )
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur(
                      "inspectionFeaturesDepths",
                      e.target.value
                    )
                  }
                  className={styles.inspectionControl}
                  placeholder="Enter inspection features and depths..."
                  disabled={disableInspectionUpdates}
                />
              </div>
            </div>

            <div className={styles.actionButtons}>
              {/* <button className={styles.resetButton} onClick={resetForm}>
                  Reset Form
                </button> */}
              <button
                className={styles.markCompleteButton}
                onClick={handleValidateAndMarkComplete}
                disabled={well.inspectionCompleted ? true : false}
              >
                {well.inspectionCompleted
                  ? "Inspection Complete"
                  : "Mark Complete"}
              </button>
            </div>
          </div>

          {/* Confirmation Modal */}
          {ui.isConfirmationModalOpen && (
            <ConfirmationModal
              onCancel={handleCloseConfirmationModal}
              onConfirm={handleConfirmMarkComplete}
              title={"Mark Inspection as Complete"}
              message={`Are you sure you want to mark this inspection for well ${well.wellId} as complete?`}
              confirmText="Mark Complete"
              cancelText="Cancel"
              styles={styles}
              confirmButtonClass="confirmMarkComplete"
            />
          )}
        </div>
      )}
    </div>
  );
};
